/* ═══════════════════════════════════════════════════════════════════════════
   WHICH PICTURE A BUSINESS SHOWS

   One function, `imageFor`, and everything that renders a picture goes through
   it. There are four places an image can come from and they are tried in this
   order, which is also their order of authority:

     1. the business's own upload          it is their page
     2. the shot the business picked       from its industry's pack
     3. the first shot in the pack         so an untouched business still
                                           gets the right kind of picture
     4. nothing                            and the caller draws the
                                           typographic hero it drew before

   Step 4 is why this is safe to add: a business, an industry or a whole pack
   can be missing and the page still renders. The picture is an improvement on
   the page, never a requirement of it.

   Note what is NOT here: no industry is named, no layout is consulted, and
   nothing decides what a picture means. A shot has a `slot` — hero, card or
   any — and that is the only hint the data gives.
   ═══════════════════════════════════════════════════════════════════════ */
import { pack, shot } from './packs.js';

/** Where the packs are served from. Vite copies `public/` to the root of the
    build, and `BASE_URL` is whatever that root turned out to be — `/` for the
    real site, `./` for a build that has to run from a subdirectory or a
    preview host. Hard-coding a leading slash here is what breaks a copy of the
    app served from anywhere other than a domain root. */
const BASE = (import.meta.env?.BASE_URL || '/').replace(/\/?$/, '/') + 'images/';

/** The URL for a manifest entry, or for a data URL the business uploaded. */
export const urlOf = entry =>
  !entry ? null : entry.custom ? entry.file : BASE + entry.file;

/**
 * The picture for one business in one slot.
 *
 * @param {object} business  needs `industry`; may carry `image` (an id from
 *                           the pack) and `image_custom` (a data URL)
 * @param {'hero'|'card'} slot
 * @returns {{url: string, alt: string, placeholder: boolean}|null}
 */
export function imageFor(business, slot = 'hero') {
  if (!business) return null;

  if (business.image_custom) {
    return {
      url: business.image_custom,
      alt: business.name || '',
      placeholder: false,
      custom: true,
    };
  }

  const rows = pack(business.industry);
  if (!rows.length) return null;

  /* Three sources, in order of how much they know about this business.
     1. the picture the tenant picked by hand — `business.image`
     2. the business's OWN photograph, if one came out of the prototype. The
        packs name those `firm-<id>`, and for thirty of the thirty-four
        businesses one exists. They were being ignored: the seeded picker
        dealt a generic shot from the trade's pack instead, which is how Oslo
        Byggservice — a builder in Brobekkveien — ended up fronted by a
        swimming pool under palm trees while its own photograph sat unused in
        the same folder. A business's own picture beats anything we can pick
        for it.
     3. the seeded pick, for a business that has no photograph yet. */
  /* Matched on `shot`, the name inside the pack, not on `id` — an id is
     "home/firm-byggservice", prefixed with the trade. */
  const own = rows.find(r => r.shot === 'firm-' + business.id) || null;
  const chosen = (business.image ? shot(business.image) : null) || own;
  const entry = chosen || seeded(rows, business, slot);

  return {
    url: BASE + entry.file,
    alt: entry.brief,
    placeholder: !!entry.placeholder,
    id: entry.id,
  };
}

/* ── What a business gets before it has chosen ───────────────────────────
   The obvious implementation — "give everyone the pack's hero shot" — puts
   the same picture on all four salons, and a marketplace page of four
   identical pictures looks worse than the four letters it replaced. It also
   teaches the tenant nothing: they never see that there is a pack.

   So the pack is dealt out. The same business always gets the same shot, and
   two businesses in one industry almost always get different ones. This is
   only the opening hand: the moment a tenant picks, their choice wins and
   this function is not consulted again for them.

   The hash is FNV-1a, the one the prototype used for exactly this reason —
   the demo must not rearrange itself between two builds. */
function hash(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function seeded(rows, business, slot) {
  /* A card wants a shot that reads at thumbnail size; a hero wants the room.
     Where the pack marks neither, everything is a candidate. */
  const preferred = rows.filter(r => r.slot === slot);
  const pool = preferred.length >= 2 ? preferred
             : rows.filter(r => r.slot === slot || r.slot === 'any');
  const list = pool.length ? pool : rows;
  /* The slot is in the seed so a business's card and its hero are not the
     same picture twice on the same screen. */
  return list[hash(business.id + '|' + slot) % list.length];
}

export { pack, shot } from './packs.js';
export { placeholders } from './packs.js';
